import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../App';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import PageContainer from '../layouts/PageContainer';
import TabContainer from '../layouts/TabContainer';
import ContentContainer from '../layouts/ContentContainer';
import { LearningPath } from '../../data/types';
import { dataService } from '../../services/dataService';
import { authService } from '../../services/authService';

const LearningPathManageScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRole } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'your' | 'shared' | 'district'>('your');
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [standardFilter, setStandardFilter] = useState<string>('');
  
  // Track the current page and handle role-specific redirects
  useEffect(() => {
    // Store the current page in sessionStorage
    sessionStorage.setItem('lastViewedPage', location.pathname);
    
    // Redirect to the appropriate role-specific learning path page if needed
    if (activeRole === 'SuperAdmin') {
      navigate('/superadmin/learning-paths', { replace: true });
    } else if (activeRole === 'SchoolAdmin') {
      navigate('/schooladmin/learning-paths', { replace: true });
    }
  }, [activeRole, location.pathname, navigate]);
  
  // Learning paths data from service
  const [mockPaths, setMockPaths] = useState<LearningPath[]>([]);
  const [sharedPaths, setSharedPaths] = useState<LearningPath[]>([]);
  const [districtPaths, setDistrictPaths] = useState<LearningPath[]>([]);
  
  // Fetch learning paths on component mount
  useEffect(() => {
    const fetchLearningPaths = async () => {
      try {
        // Get the current user from authService
        const currentUser = authService.getCurrentUser();
        const paths = await dataService.getLearningPaths();
        
        // Only show paths created by the current teacher
        setMockPaths(paths.filter(path => 
          !path.isShared && 
          !path.isDistrict && 
          path.creatorId === currentUser?.id
        ));
        setSharedPaths(paths.filter(path => path.isShared));
        setDistrictPaths(paths.filter(path => path.isDistrict));
      } catch (error) {
        console.error('Error fetching learning paths:', error);
      }
    };

    fetchLearningPaths();
  }, []);

  // Handle path selection
  const handlePathSelect = (pathId: string) => {
    // If clicking on own path, navigate to edit
    if (activeTab === 'your') {
      navigate(`/learning-paths/edit/${pathId}`);
    } 
    // For shared and district paths, navigate to a clone view
    else {
      // In a real app, you would determine if this needs to be cloned first
      // For demo, just pretend we're navigating to a view where we can clone
      navigate(`/learning-paths/clone/${pathId}`);
    }
  };

  // Create new path
  const handleCreateNewPath = () => {
    navigate('/learning-paths/create');
  };

  // Handle path assignment
  const handleAssignPath = (pathId: string) => {
    navigate(`/learning-paths/assign?pathId=${pathId}`);
  };

  // List of possible grades for filter
  const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setGradeFilter('');
    setDifficultyFilter('');
    setStandardFilter('');
  };

  // Get active paths based on tab
  const getActivePaths = () => {
    switch (activeTab) {
      case 'your':
        return mockPaths;
      case 'shared':
        return sharedPaths;
      case 'district':
        return districtPaths;
      default:
        return mockPaths;
    }
  };

  // Apply filters to paths
  const getFilteredPaths = () => {
    let filteredPaths = getActivePaths();
    
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredPaths = filteredPaths.filter(path => 
        path.name.toLowerCase().includes(query) || 
        path.description.toLowerCase().includes(query)
      );
    }
    
    // Apply grade filter
    if (gradeFilter) {
      filteredPaths = filteredPaths.filter(path => path.grade === gradeFilter);
    }
    
    // Apply difficulty filter
    if (difficultyFilter) {
      filteredPaths = filteredPaths.filter(path => path.difficulty === difficultyFilter);
    }
    
    // Apply standard filter - simplified for demo
    if (standardFilter) {
      filteredPaths = filteredPaths.filter(path => 
        path.standards.some(std => std.standardId.includes(standardFilter))
      );
    }
    
    return filteredPaths;
  };

  // Get color for difficulty badge
  const getDifficultyColor = (difficulty: 'Easy' | 'Medium' | 'Hard') => {
    switch (difficulty) {
      case 'Easy':
        return 'success';
      case 'Medium':
        return 'warning';
      case 'Hard':
        return 'error';
      default:
        return 'primary';
    }
  };

  const createPathButton = (
    <Button
      variant="primary"
      onClick={handleCreateNewPath}
    >
      <span className="flex items-center">
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Create New Path
      </span>
    </Button>
  );

  return (
    <PageContainer title="Learning Paths" actionButton={createPathButton} userRole="Teacher">
      
      <TabContainer
        tabContent={
          <div className="flex space-x-4 py-2">
            <button
              className={`px-4 py-2 font-medium text-sm rounded-md ${activeTab === 'your' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('your')}
            >
              My Learning Paths
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm rounded-md ${activeTab === 'shared' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('shared')}
            >
              Shared Paths
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm rounded-md ${activeTab === 'district' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('district')}
            >
              District Paths
            </button>
          </div>
        }
        filterContent={
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search paths..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <option value="">All Grades</option>
                {grades.map(grade => (
                  <option key={grade} value={grade}>
                    {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">All Difficulty Levels</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={standardFilter}
                onChange={(e) => setStandardFilter(e.target.value)}
              >
                <option value="">All Standards</option>
                <option value="CCSS">Common Core</option>
                <option value="NGSS">Next Generation Science</option>
                <option value="State">State Standards</option>
              </select>
            </div>
            
            <div className="md:col-span-2 flex items-center">
              <button
                onClick={resetFilters}
                className="text-primary hover:text-primary-dark"
              >
                Reset Filters
              </button>
            </div>
          </div>
        }
      >
        {activeTab === 'your' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[calc(100vh-350px)] overflow-y-auto pr-2">
            {getFilteredPaths().map(path => (
              <div 
                key={path.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-base font-medium">{path.name}</h3>
                    <p className="text-xs text-gray-600 line-clamp-2 mt-1">{path.description}</p>
                  </div>
                  <Badge variant={getDifficultyColor(path.difficulty ?? 'Medium')}>
                    {path.difficulty ?? 'Medium'}
                  </Badge>
                </div>
                
                <div className="mt-2 text-xs text-gray-500">
                  <div className="flex items-center">
                    <span>Grade {path.grade}</span>
                    <span className="mx-2">•</span>
                    <span>{path.standards.length} Standards</span>
                    <span className="mx-2">•</span>
                    <span>Created {new Date(path.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-2">
                  {activeRole !== 'SchoolAdmin' && (
                    <button 
                      className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePathSelect(path.id);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  <button 
                    className="px-2 py-1 text-xs text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignPath(path.id);
                    }}
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
            
            {getFilteredPaths().length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No learning paths found matching your search.
              </div>
            )}
          </div>
        )}
        
        {(activeTab === 'shared' || activeTab === 'district') && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[calc(100vh-350px)] overflow-y-auto pr-2">
            {getFilteredPaths().map(path => (
              <div 
                key={path.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-base font-medium">{path.name}</h3>
                    <p className="text-xs text-gray-600 line-clamp-2 mt-1">{path.description}</p>
                  </div>
                  <Badge variant={getDifficultyColor(path.difficulty ?? 'Medium')}>
                    {path.difficulty ?? 'Medium'}
                  </Badge>
                </div>
                
                <div className="mt-2 text-xs text-gray-500">
                  <div className="flex items-center">
                    <span>Grade {path.grade}</span>
                    <span className="mx-2">•</span>
                    <span>{path.standards.length} Standards</span>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-2">
                  <button 
                    className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                    onClick={() => handlePathSelect(path.id)}
                  >
                    Clone
                  </button>
                  <button 
                    className="px-2 py-1 text-xs text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                    onClick={() => handleAssignPath(path.id)}
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
            
            {getFilteredPaths().length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No learning paths found matching your search.
              </div>
            )}
          </div>
        )}
      </TabContainer>
    </PageContainer>
  );
};

export default LearningPathManageScreen;
